import React, { useState, useEffect } from "react";
import { User, Phone, Car, Wrench, FileText, CheckCircle2, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

/**
 * Validates either standard 10-digit Iranian National ID or 
 * Foreign National ID format (e.g., F026720956).
 * Total length is always exactly 10 characters.
 */
export function isValidNationalId(code) {
  if (!code || typeof code !== "string") return false;
  const clean = code.trim().toUpperCase();
  return /^(\d{10}|[A-Z]\d{9})$/.test(clean);
}

export default function StepPersonalInfo({ formData, updateFormData, onNext, onPrev }) {
  const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "/api/";
  const BOOKING_BASE = "/api/booking/";
  const BASIC_BASE = "/api/booking/BasicInformation/";

  const [isVinValid, setIsVinValid] = useState(false);
  const [isValidatingVin, setIsValidatingVin] = useState(false);
  const [isValidatingNext, setIsValidatingNext] = useState(false);

  const [isFormRevealed, setIsFormRevealed] = useState(false);
  const [allocatedCars, setAllocatedCars] = useState([]);
  const [isFetchingCars, setIsFetchingCars] = useState(false);
  const [showManualVin, setShowManualVin] = useState(true);
  const [carsFetchedForNationalId, setCarsFetchedForNationalId] = useState("");


  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);

  useEffect(() => {
    // Fetch Vehicle Types on mount
    const fetchTypes = async () => {
      try {
        const res = await fetch(`${BASIC_BASE}GetAllVehicleTypes`);
        if (res.ok) {
          const data = await res.json();
          setVehicleTypes(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch vehicle types", err);
      }
    };
    fetchTypes();
  }, []);

  const fetchModels = async (typeId) => {
    try {
      const res = await fetch(`${BASIC_BASE}GetAllVehicleModels?VehicleTypeId=${typeId}`);
      if (res.ok) {
        const data = await res.json();
        setVehicleModels(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to fetch models", err);
    }
  };

  const fetchCarsForNationalId = async (nationalId) => {
    if (!nationalId || !isValidNationalId(nationalId)) return;
    if (nationalId === carsFetchedForNationalId) return;

    setIsFetchingCars(true);
    setCarsFetchedForNationalId(nationalId);

    const QR_URL = process.env.NEXT_PUBLIC_API_QR_URL || 'https://api.barsavosh.co/api2/';
    const cleanQrUrl = QR_URL.endsWith('/') ? QR_URL : `${QR_URL}/`;

    try {
      const res = await fetch(`${cleanQrUrl}customers/external-cars/${nationalId}`, {
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.AllocatedCars && data.AllocatedCars.length > 0) {
          setAllocatedCars(data.AllocatedCars);
          setShowManualVin(false);
          updateFormData({ vin: "" }); // Reset vin when switching to dropdown
        } else {
          setAllocatedCars([]);
          setShowManualVin(true);
        }
      } else {
        setAllocatedCars([]);
        setShowManualVin(true);
      }
    } catch (e) {
      setAllocatedCars([]);
      setShowManualVin(true);
    } finally {
      setIsFetchingCars(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalVal = value;

    // Normalizing persian digits to english digits for numbers
    const persian = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    for (let i = 0; i < 10; i++) {
      finalVal = finalVal.replace(persian[i], i);
    }

    if (name === "nationalCode") {
      // 1. Keep English letters and numbers, force uppercase, cap at 10 chars
      const cleanVal = finalVal
        .replace(/[^0-9a-zA-Z]/g, "")
        .toUpperCase()
        .slice(0, 10);
      updateFormData({ [name]: cleanVal });
      setIsFormRevealed(false);
      // 2. Trigger auto-fetch only if it matches valid Iranian or Foreign format
      if (isValidNationalId(cleanVal)) {
        fetchCarsForNationalId(cleanVal);
      }
      return;
    }

    if (name === "vin") {
      finalVal = finalVal.replace(/[^0-9a-zA-Z]/g, "").toUpperCase();
      setIsVinValid(false);
      setIsFormRevealed(false);
      updateFormData({ [name]: finalVal });
      return;
    }

    if (name === "vinSelect") {
      if (value === "manual") {
        setShowManualVin(true);
        updateFormData({ vin: "" });
        setIsVinValid(false);
        setIsFormRevealed(false);
      } else {
        setShowManualVin(false);
        updateFormData({ vin: value });
        setIsVinValid(false);
        setIsFormRevealed(false);
      }
      return;
    }

    if (["mobile", "kilometer", "platePart1", "platePart2", "plateCityCode"].includes(name)) {
      finalVal = finalVal.replace(/[^0-9]/g, "");
    }

    updateFormData({ [name]: finalVal });

    // Handle cascading dropdown for Vehicle Type -> Model
    if (name === "vehicleTypeId") {
      updateFormData({ vehicleModelId: "" });
      setVehicleModels([]);
      if (finalVal) fetchModels(finalVal);
    }
  };

  const validateVin = async () => {
    if (!formData.vin || formData.vin.length !== 17) {
      Swal.fire({ title: "خطا", text: "شماره شاسی باید ۱۷ کاراکتر باشد.", icon: "warning", confirmButtonColor: "#2b2b2b" });
      return;
    }

    if (!formData.nationalCode || !isValidNationalId(formData.nationalCode)) {
      Swal.fire({ title: "توجه", text: "برای بررسی شاسی و دریافت اطلاعات، لطفا ابتدا کدملی را به درستی وارد کنید.", icon: "info", confirmButtonColor: "#2b2b2b" });
      return;
    }

    setIsValidatingVin(true);
    try {
      // 1. Check if VIN is valid (exists in company)
      const resValid = await fetch(`${BOOKING_BASE}Booking/IsValidVinnumber?VinNumber=${formData.vin}`);
      const isValid = await resValid.json();

      if (!isValid) {
        Swal.fire({ title: "خطا", text: "شماره شاسی وارد شده در سیستم برساوش یافت نشد.", icon: "error", confirmButtonColor: "#2b2b2b" });
        setIsVinValid(false);
        return;
      }

      // 2. Check if open reservation exists
      const resExists = await fetch(`${BOOKING_BASE}Booking/CheckExistsReserveVinNumber?VinNumber=${formData.vin}`);
      const existsText = await resExists.text();
      let existsData = {};
      try { existsData = JSON.parse(existsText); } catch (e) { }

      if (existsData === true || existsText === "true") {
        Swal.fire({ title: "خطا", text: "برای این شماره شاسی، یک نوبت باز وجود دارد.", icon: "error", confirmButtonColor: "#2b2b2b" });
        setIsVinValid(false);
        return;
      }

      setIsVinValid(true);

      // 3. Try to fetch personal info & evaluate with National Code
      try {
        const resInfo = await fetch(`${BOOKING_BASE}Booking/getSubscriberChassisAllocation?VinNumber=${formData.vin}`);
        if (resInfo.ok) {
          const text = await resInfo.text();
          if (text) {
            const info = JSON.parse(text);

            // Evaluate if fetched National Code matches the user's input National Code
            if (
              info.SubscriberNationalCode &&
              info.SubscriberNationalCode.toUpperCase() === formData.nationalCode.toUpperCase()
            ) {
              const updates = {};
              if (info.SubscriberFirstName) updates.firstName = info.SubscriberFirstName;
              if (info.SubscriberLastName) updates.lastName = info.SubscriberLastName;
              if (info.SubscriberMobile) updates.mobile = info.SubscriberMobile;

              if (info.VehicleTypeId) {
                updates.vehicleTypeId = info.VehicleTypeId;
                fetchModels(info.VehicleTypeId);
              }
              if (info.ChassisVehicleModelId) updates.vehicleModelId = info.ChassisVehicleModelId;

              const plateLetterMap = {
                1: "الف", 2: "ب", 3: "پ", 4: "ت", 5: "ث", 6: "ج", 7: "چ", 8: "ح", 9: "خ", 10: "د",
                11: "ذ", 12: "ر", 13: "ز", 14: "ژ", 15: "س", 16: "ش", 17: "ص", 18: "ض", 19: "ط", 20: "ظ",
                21: "ع", 22: "غ", 23: "ف", 24: "ق", 25: "ک", 26: "گ", 27: "ل", 28: "م", 29: "ن", 30: "و",
                31: "ه", 32: "ی"
              };

              if (info.ChassisPlateVm) {
                updates.platePart1 = String(info.ChassisPlateVm['<PartOne>k__BackingField'] || '');

                const letterCode = info.ChassisPlateVm['<PartTwo>k__BackingField'];
                updates.plateLetter = plateLetterMap[letterCode] || '';

                updates.platePart2 = String(info.ChassisPlateVm['<PartThree>k__BackingField'] || '');
                updates.plateCityCode = String(info.ChassisPlateVm['<PartFour>k__BackingField'] || '');
              }

              if (Object.keys(updates).length > 0) {
                updateFormData(updates);
                setIsFormRevealed(true);
                Swal.fire({
                  toast: true,
                  position: 'top-end',
                  showConfirmButton: false,
                  timer: 4000,
                  icon: 'success',
                  title: 'اطلاعات شما با موفقیت تطبیق داده شد و فرم تکمیل گردید.'
                });
              } else {
                setIsFormRevealed(true);
              }
            } else {
              // VIN belongs to company, but national code doesn't match the owner
              setIsFormRevealed(true);
              Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 5000,
                icon: 'info',
                title: 'کدملی با مالک خودرو تطابق ندارد. لطفا فرم را به صورت دستی تکمیل کنید.'
              });
            }
          } else {
            setIsFormRevealed(true);
          }
        } else {
          setIsFormRevealed(true);
        }
      } catch (e) {
        console.error("Could not fetch personal info", e);
        setIsFormRevealed(true);
      }

    } catch (err) {
      console.error(err);
      Swal.fire({ title: "خطا", text: "خطا در ارتباط با سرور", icon: "error", confirmButtonColor: "#2b2b2b" });
    } finally {
      setIsValidatingVin(false);
    }
  };

  const handleNext = async () => {
    // Validations
    if (!formData.firstName || !formData.lastName) return Swal.fire({ title: "خطا", text: "نام و نام خانوادگی الزامی است", icon: "warning", confirmButtonColor: "#2b2b2b" });
    if (!formData.nationalCode || !isValidNationalId(formData.nationalCode)) return Swal.fire({ title: "خطا", text: "کدملی وارد شده معتبر نمیباشد", icon: "warning", confirmButtonColor: "#2b2b2b" });
    if (!formData.mobile.startsWith("09") || formData.mobile.length !== 11) return Swal.fire({ title: "خطا", text: "شماره موبایل صحیح نیست", icon: "warning", confirmButtonColor: "#2b2b2b" });
    if (!isVinValid) return Swal.fire({ title: "خطا", text: "لطفا شماره شاسی را بررسی کنید", icon: "warning", confirmButtonColor: "#2b2b2b" });
    if (!formData.vehicleModelId) return Swal.fire({ title: "خطا", text: "انتخاب مدل خودرو الزامی است", icon: "warning", confirmButtonColor: "#2b2b2b" });
    if (!formData.kilometer || formData.kilometer.trim() === "") return Swal.fire({ title: "خطا", text: "کارکرد خودرو (کیلومتر) الزامی است", icon: "warning", confirmButtonColor: "#2b2b2b" });
    if (!formData.platePart1 || !formData.plateLetter || !formData.platePart2 || !formData.plateCityCode) {
      return Swal.fire({ title: "خطا", text: "ورود کامل پلاک الزامی است", icon: "warning", confirmButtonColor: "#2b2b2b" });
    }
    if (!formData.description || formData.description.trim() === "") {
      return Swal.fire({ title: "خطا", text: "لطفا توضیحات ایرادات خودرو را وارد کنید", icon: "warning", confirmButtonColor: "#2b2b2b" });
    }

    setIsValidatingNext(true);
    try {
      const res = await fetch(`${BOOKING_BASE}Booking/CheckIsValidKilometer?VinNumber=${formData.vin}&Kilometer=${formData.kilometer}`);
      if (res.ok) {
        const text = await res.text();
        if (text === "false" || text === false) {
          Swal.fire({
            title: "خطا",
            text: "کیلومتر وارد شده از کیلومتر ثبت شده در سیستم کمتر می باشد ، لطفا اطلاعات را با دقت وارد نمایید.",
            icon: "error",
            confirmButtonColor: "#2b2b2b"
          });
          return;
        }
      }
    } catch (err) {
      console.error("Error validating kilometer:", err);
    } finally {
      setIsValidatingNext(false);
    }

    onNext();
  };

  const letters = ["ب", "ج", "د", "س", "ص", "ط", "ق", "ل", "م", "ن", "و", "ه", "ی"];

  return (
    <div className="flex flex-col h-full animate-in fade-in slide-in-from-right-4 duration-500">
      <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
        <div className="bg-gray-100 p-2.5 rounded-xl text-[#2b2b2b]">
          <User size={24} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">اطلاعات فردی و خودرو</h2>
          <p className="text-sm text-slate-500 mt-1">
            مشخصات فردی و اطلاعات وسیله نقلیه خود را وارد کنید
          </p>
        </div>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto pr-2 custom-scrollbar pb-6">

        {/* Verification Check */}
        <div className="bg-gray-50/50 p-4 sm:p-6 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-5">
            <span className="bg-[#2b2b2b] text-white p-1.5 rounded-lg"><Car size={16} /></span>
            استعلام و تکمیل خودکار اطلاعات
          </div>
          <div className="flex flex-col gap-5 w-full">

            {/* National Code Row */}
            <div className="w-full space-y-2">
              <label className="text-xs font-semibold text-slate-600">کد ملی مالک <span className="text-red-500">*</span></label>
              <div className="relative">
                <input
                  name="nationalCode"
                  value={formData.nationalCode}
                  onChange={handleInputChange}
                  dir="ltr"
                  maxLength={10}
                  placeholder="۱۰ رقم"
                  className="w-full px-4 py-3.5 rounded-xl border-2 font-bold text-sm text-right bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none transition-all"
                />
                {isFetchingCars && (
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <Loader2 size={20} className="animate-spin text-[#2b2b2b]" />
                  </div>
                )}
              </div>
            </div>

            {/* VIN Row (Appears after National ID is entered) */}
            {isValidNationalId(formData.nationalCode) && (
              <div className="w-full space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                <label className="text-xs font-semibold text-slate-600">شماره شاسی (VIN) <span className="text-red-500">*</span></label>
                
                {allocatedCars.length > 0 && !showManualVin ? (
                  <select
                    name="vinSelect"
                    value={formData.vin || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3.5 rounded-xl border-2 font-bold text-sm text-right bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none transition-all cursor-pointer"
                  >
                    <option value="">انتخاب خودرو...</option>
                    {allocatedCars.map((car, idx) => (
                      <option key={car.VinNumber + idx} value={car.VinNumber}>{car.VinNumber} - {car.Model || 'نامشخص'}</option>
                    ))}
                    <option value="manual">+ سایر (وارد کردن دستی شماره شاسی)</option>
                  </select>
                ) : (
                  <input
                    name="vin"
                    value={formData.vin}
                    onChange={handleInputChange}
                    placeholder="شماره ۱۷ رقمی شاسی"
                    dir="ltr"
                    maxLength={17}
                    className="w-full px-4 py-3.5 rounded-xl border-2 uppercase font-bold text-sm text-right bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none transition-all"
                  />
                )}
              </div>
            )}

            {/* Validate Button Row (Appears after VIN is entered) */}
            {isValidNationalId(formData.nationalCode) && formData.vin.length === 17 && (
              <div className="w-full pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                {isVinValid && isFormRevealed ? (
                  <div className="flex items-center justify-center gap-2 px-6 py-4 bg-gray-300 text-black border border-gray-400/40 rounded-xl font-bold w-full">
                    <CheckCircle2 size={20} />
                    شاسی و کدملی با موفقیت تایید شد
                  </div>
                ) : (
                  <button
                    onClick={validateVin}
                    disabled={isValidatingVin}
                    className="w-full bg-[#2b2b2b] hover:bg-neutral-800 disabled:bg-slate-400 text-white font-bold px-8 py-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-neutral-900/20"
                  >
                    {isValidatingVin && <Loader2 size={18} className="animate-spin" />}
                    بررسی و ادامه
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Revealed Form Section */}
        {isFormRevealed && (
          <div className="space-y-6 mt-8 animate-in fade-in slide-in-from-top-4 duration-500">
            {/* Personal Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700">نام <span className="text-red-500">*</span></label>
                <input name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700">نام خانوادگی <span className="text-red-500">*</span></label>
                <input name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none" />
              </div>
              <div className="space-y-1.5 sm:col-span-2 md:col-span-1">
                <label className="text-sm font-semibold text-slate-700">شماره موبایل <span className="text-red-500">*</span></label>
                <input name="mobile" value={formData.mobile} onChange={handleInputChange} dir="ltr" maxLength={11} placeholder="09" className="w-full px-4 py-3 rounded-xl border-2 text-sm text-right bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none" />
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700">نوع وسیله نقلیه <span className="text-red-500">*</span></label>
                <select name="vehicleTypeId" value={formData.vehicleTypeId || ""} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none">
                  <option value="">انتخاب کنید</option>
                  {vehicleTypes.map((t, i) => <option key={t.UniqueId || i} value={t.UniqueId}>{t.VehicleTypeFullName || t.VehicleTypeLocalizedName}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700">مدل وسیله نقلیه <span className="text-red-500">*</span></label>
                <select name="vehicleModelId" value={formData.vehicleModelId} onChange={handleInputChange} disabled={!formData.vehicleTypeId} className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none disabled:bg-slate-50">
                  <option value="">انتخاب کنید</option>
                  {vehicleModels.map((m, i) => <option key={m.UniqueId || i} value={m.UniqueId}>{m.VehicleModelFullName || m.VehicleModelLocalizeName}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700">کارکرد خودرو (کیلومتر) <span className="text-red-500">*</span></label>
                <input name="kilometer" value={formData.kilometer} onChange={handleInputChange} dir="ltr" className="w-full px-4 py-3 rounded-xl border-2 text-sm text-right bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none" />
              </div>
            </div>

            {/* Plate Number */}
            <div className="space-y-1.5 mt-2">
              <label className="text-sm font-semibold text-slate-700">پلاک خودرو <span className="text-red-500">*</span></label>
              <div dir="ltr" className="flex w-full max-w-sm border-2 border-slate-200 rounded-xl overflow-hidden bg-white items-stretch focus-within:border-[#2b2b2b] focus-within:ring-1 focus-within:ring-[#2b2b2b] transition-all h-[52px]">
                <div className="bg-[#2b2b2b] w-10 flex flex-col items-center justify-center text-white font-bold text-[10px] shrink-0 border-r border-slate-200">
                  <span>I.R.</span><span>IRAN</span>
                </div>
                <input type="text" name="platePart1" value={formData.platePart1} onChange={handleInputChange} className="flex-1 min-w-0 text-center font-bold outline-none bg-transparent" maxLength={2} placeholder="12" />
                <select name="plateLetter" value={formData.plateLetter} onChange={handleInputChange} className="flex-[1.2] min-w-0 text-center font-bold outline-none bg-slate-50 cursor-pointer border-x border-slate-100 appearance-none text-slate-700 px-0" dir="rtl">
                  <option value="">--</option>
                  {letters.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <input type="text" name="platePart2" value={formData.platePart2} onChange={handleInputChange} className="flex-[1.5] min-w-0 text-center font-bold outline-none bg-transparent" maxLength={3} placeholder="345" />
                <div className="w-px bg-slate-300 my-2 shrink-0"></div>
                <div className="flex-[1.2] min-w-0 flex flex-col justify-center items-center relative">
                  <span className="text-[9px] text-slate-400 absolute top-0.5 inset-x-0 text-center">ایران</span>
                  <input type="text" name="plateCityCode" value={formData.plateCityCode} onChange={handleInputChange} className="w-full text-center font-bold outline-none bg-transparent pt-3" maxLength={2} placeholder="67" />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5 mt-2">
              <label className="text-sm font-semibold text-slate-700">توضیحات ایرادات <span className="text-red-500">*</span></label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none resize-none"></textarea>
            </div>
          </div>
        )}

      </div>

      <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-6">
        <button onClick={onPrev} className="flex items-center gap-2 py-3 px-6 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all">
          <ArrowRight size={18} /> قبلی
        </button>
        <button
          onClick={handleNext}
          disabled={isValidatingNext}
          className="flex items-center gap-2 py-3 px-8 rounded-xl font-bold text-white bg-[#2b2b2b] hover:bg-neutral-800 shadow-lg shadow-neutral-900/20 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isValidatingNext ? (
            <><Loader2 size={18} className="animate-spin" /> در حال بررسی...</>
          ) : (
            <>مرحله بعد <ArrowLeft size={18} /></>
          )}
        </button>
      </div>
    </div>
  );
}
